import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, RefreshCw, Calendar, Trophy, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { Navigation } from "../components/sections/Navigation";
import { Footer } from "../components/sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FKF_URL = "https://www.footballkenya.org/sportpesa-premier-league";

const formatLongDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (err) {
    return iso;
  }
};

const formatTime = (timeStr) => {
  if (!timeStr) return null;
  // expects HH:MM or HH:MM:SS
  const [h, m] = timeStr.split(":");
  if (!h || !m) return timeStr;
  return `${h}:${m}`;
};

const formatStamp = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (err) {
    return iso;
  }
};

const TeamBadge = ({ team, align = "right" }) => {
  if (!team) {
    return <span className="text-[#0A0A0A]/40">TBD</span>;
  }
  return (
    <div className={`flex items-center gap-3 ${align === "right" ? "justify-end" : ""}`}>
      {align === "right" && (
        <span className="font-semibold text-[#0A0A0A] text-sm sm:text-base text-right">
          {team.team_name}
        </span>
      )}
      {team.logo_url ? (
        <img
          src={team.logo_url}
          alt={`${team.team_name} crest`}
          className="w-8 h-8 object-contain"
        />
      ) : (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0A0A0A]/5 text-[10px] font-bold uppercase text-[#0A0A0A]/55">
          {team.short_name || (team.team_name || "?").slice(0, 2)}
        </span>
      )}
      {align === "left" && (
        <span className="font-semibold text-[#0A0A0A] text-sm sm:text-base">
          {team.team_name}
        </span>
      )}
    </div>
  );
};

const MatchRow = ({ match, type, idx }) => {
  const isResult = type === "results";
  const time = formatTime(match.match_time);
  const isPostponed = match.status === "postponed";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(idx * 0.02, 0.3) }}
      className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-6 py-4 px-3 sm:px-5 border-b border-[#E4E4E7] hover:bg-[#FAFAFA] transition-colors"
      data-testid={`match-row-${match.id}`}
    >
      {/* Home team — right-aligned */}
      <div className="min-w-0">
        <TeamBadge team={match.home_team} align="right" />
      </div>

      {/* Centre — score or kickoff */}
      <div className="flex flex-col items-center min-w-[88px]">
        {isResult ? (
          <div
            className="inline-flex items-center bg-[#0A0A0A] text-white px-3 py-1.5 font-bold text-sm sm:text-base tracking-wider"
            data-testid={`match-score-${match.id}`}
          >
            <span>{match.home_score ?? 0}</span>
            <span className="mx-2 opacity-50">—</span>
            <span>{match.away_score ?? 0}</span>
          </div>
        ) : isPostponed ? (
          <span
            className="inline-flex items-center bg-[#D1111A] text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            data-testid={`match-postponed-${match.id}`}
          >
            Postponed
          </span>
        ) : (
          <span
            className="inline-flex items-center border border-[#0A0A0A]/15 text-[#0A0A0A] px-3 py-1.5 font-bold text-sm tracking-wider"
            data-testid={`match-time-${match.id}`}
          >
            {time || "TBD"}
          </span>
        )}
        {match.matchday != null && (
          <span className="mt-1 text-[9px] uppercase tracking-[0.2em] text-[#0A0A0A]/45">
            MD {match.matchday}
          </span>
        )}
      </div>

      {/* Away team */}
      <div className="min-w-0">
        <TeamBadge team={match.away_team} align="left" />
      </div>
    </motion.div>
  );
};

const SkeletonRows = () => (
  <div className="space-y-3" data-testid="matches-loading">
    {Array.from({ length: 8 }).map((_, idx) => (
      <div key={idx} className="h-16 w-full animate-pulse bg-[#0A0A0A]/5 rounded" />
    ))}
  </div>
);

const groupByDate = (matches) => {
  const map = new Map();
  matches.forEach((m) => {
    const key = m.match_date || "unknown";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  });
  return Array.from(map.entries());
};

export const MatchesPage = ({ type }) => {
  const isResults = type === "results";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [matchday, setMatchday] = useState(null);

  const load = async ({ refresh = false, md = null } = {}) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ type, limit: "500" });
      if (refresh) params.set("force", "true");
      if (md != null) params.set("matchday", String(md));
      const res = await fetch(`${API}/matches?${params.toString()}`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const json = await res.json();
      setData(json);
      setMatchday(json.current_matchday);
    } catch (err) {
      console.error(err);
      setError(
        `We couldn't load the live ${isResults ? "results" : "fixtures"} right now. Please try again in a moment.`,
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setMatchday(null);
    load({ refresh: false, md: null });
  }, [type]);

  const goPrev = () => {
    if (data?.prev_matchday != null) load({ md: data.prev_matchday });
  };
  const goNext = () => {
    if (data?.next_matchday != null) load({ md: data.next_matchday });
  };

  const grouped = useMemo(() => groupByDate(data?.matches || []), [data]);
  const fetchedAt = useMemo(() => formatStamp(data?.fetched_at), [data]);

  const eyebrow = isResults ? "Results" : "Fixtures";
  const title = isResults ? "Latest Results" : "Upcoming Fixtures";
  const intro = isResults
    ? "Final scores from the most recent matchday of the Sportpesa Premier League, syncing live from the Football Kenya Federation."
    : "The next matchday of the Sportpesa Premier League — kickoff times, venues and any postponements, syncing live from the Football Kenya Federation.";
  const Icon = isResults ? Trophy : Calendar;

  return (
    <div className="App bg-[#FAFAFA] min-h-screen" data-testid={`${type}-page`}>
      <Navigation />

      {/* Hero */}
      <section
        className="pt-32 pb-12 px-6 bg-white border-b border-[#E4E4E7]"
        data-testid={`${type}-hero`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.32em] text-[#D1111A] mb-6">
            <Icon size={14} />
            <span>Sportpesa Premier League · {eyebrow}</span>
          </div>
          <h1
            className="font-bold text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid={`${type}-title`}
          >
            {title}
          </h1>
          <p className="mt-6 max-w-2xl text-base text-[#0A0A0A]/70 leading-relaxed">{intro}</p>

          <div className="mt-8 flex flex-wrap items-center gap-6 text-xs uppercase tracking-[0.18em] text-[#0A0A0A]/55">
            {data?.season && (
              <span data-testid={`${type}-season`}>
                <span className="text-[#0A0A0A]/40 mr-2">Season</span>
                <span className="text-[#0A0A0A] font-bold">{data.season}</span>
              </span>
            )}
            {data?.matches && (
              <span data-testid={`${type}-count`}>
                <span className="text-[#0A0A0A]/40 mr-2">{eyebrow}</span>
                <span className="text-[#0A0A0A] font-bold">{data.matches.length}</span>
              </span>
            )}
            <button
              type="button"
              onClick={() => load({ refresh: true, md: matchday })}
              disabled={refreshing || loading}
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#0A0A0A]/15 hover:border-[#D1111A] hover:text-[#D1111A] transition-colors disabled:opacity-50"
              data-testid={`${type}-refresh-button`}
            >
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing" : "Refresh"}
            </button>
          </div>
        </div>
      </section>

      {/* Matchday stepper — hide when empty */}
      {data?.matches?.length > 0 && (
        <section className="px-6 -mt-px" data-testid={`${type}-stepper-section`}>
        <div className="max-w-6xl mx-auto">
          <div
            className="flex items-center justify-between gap-3 border-x border-b border-[#E4E4E7] bg-white px-4 sm:px-6 py-4"
            data-testid={`${type}-stepper`}
          >
            <button
              type="button"
              onClick={goPrev}
              disabled={loading || refreshing || data?.prev_matchday == null}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] hover:text-[#D1111A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid={`${type}-prev-button`}
              aria-label="Previous matchday"
            >
              <ChevronLeft size={16} />
              <span className="hidden sm:inline">
                {isResults ? "Past Results" : "Earlier"}
              </span>
              {data?.prev_matchday != null && (
                <span className="text-[#0A0A0A]/55 normal-case tracking-normal">
                  · MD{data.prev_matchday}
                </span>
              )}
            </button>

            <div
              className="flex flex-col items-center text-center"
              data-testid={`${type}-current-matchday`}
            >
              <span className="text-[9px] font-bold uppercase tracking-[0.28em] text-[#D1111A]">
                Matchday
              </span>
              <span
                className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {data?.current_matchday ?? "—"}
              </span>
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={loading || refreshing || data?.next_matchday == null}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] hover:text-[#D1111A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid={`${type}-next-button`}
              aria-label="Next matchday"
            >
              {data?.next_matchday != null && (
                <span className="text-[#0A0A0A]/55 normal-case tracking-normal">
                  MD{data.next_matchday} ·
                </span>
              )}
              <span className="hidden sm:inline">
                {isResults ? "Newer" : "Next Fixtures"}
              </span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>
      )}

      {/* Matches */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="border border-[#E4E4E7] bg-white p-4 sm:p-8 shadow-sm">
            {loading && <SkeletonRows />}

            {!loading && error && (
              <div
                className="flex flex-col items-start gap-4 p-6 border-l-4 border-[#D1111A] bg-[#D1111A]/5"
                data-testid={`${type}-error`}
              >
                <div className="flex items-center gap-2 text-[#D1111A] font-bold uppercase tracking-[0.18em] text-xs">
                  <AlertTriangle size={14} /> Live data unavailable
                </div>
                <p className="text-sm text-[#0A0A0A]/75">{error}</p>
                <button
                  type="button"
                  onClick={() => load({ refresh: true, md: matchday })}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#D1111A] transition-colors"
                  data-testid={`${type}-retry-button`}
                >
                  Try again
                </button>
              </div>
            )}

            {!loading && !error && grouped.length === 0 && (
              <p
                className="text-center py-8 text-[#0A0A0A]/55"
                data-testid={`${type}-empty`}
              >
                {isResults
                  ? "No completed matches yet for this season."
                  : "No upcoming fixtures scheduled."}
              </p>
            )}

            {!loading && !error && grouped.length > 0 && (
              <div className="space-y-10" data-testid={`${type}-list`}>
                {grouped.map(([date, matches]) => (
                  <div key={date} data-testid={`${type}-group-${date}`}>
                    <div className="flex items-baseline gap-3 pb-3 mb-3 border-b-2 border-[#0A0A0A]">
                      <span className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#D1111A]">
                        {isResults ? "Played" : "Kickoff"}
                      </span>
                      <h2
                        className="text-xl sm:text-2xl text-[#0A0A0A]"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {formatLongDate(date) || "Date TBD"}
                      </h2>
                    </div>
                    <div className="divide-y divide-[#E4E4E7]">
                      {matches.map((m, idx) => (
                        <MatchRow key={m.id} match={m} type={type} idx={idx} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs uppercase tracking-[0.18em] text-[#0A0A0A]/55">
            <span data-testid={`${type}-source`}>
              Source ·{" "}
              <a
                href={FKF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#0A0A0A] hover:text-[#D1111A] transition-colors normal-case tracking-normal"
                data-testid={`${type}-source-link`}
              >
                Football Kenya Federation
                <ArrowUpRight size={12} />
              </a>
            </span>
            {fetchedAt && (
              <span
                data-testid={`${type}-fetched-at`}
                className="normal-case tracking-normal"
              >
                Fetched {fetchedAt}
              </span>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export const FixturesPage = () => <MatchesPage type="fixtures" />;
export const ResultsPage = () => <MatchesPage type="results" />;

export default MatchesPage;
